import React from 'react';
import { format } from 'date-fns';

interface WeekCircleProps {
  completedDates: string[];
  color: string;
  size?: number;
}

const COLOR_MAP: Record<string, string> = {
  'bg-red-500': '#EF4444',
  'bg-orange-500': '#F97316',
  'bg-amber-500': '#F59E0B',
  'bg-yellow-500': '#EAB308',
  'bg-green-500': '#22C55E',
  'bg-emerald-500': '#10B981',
  'bg-teal-500': '#14B8A6',
  'bg-cyan-500': '#06B6D4',
  'bg-blue-500': '#3B82F6',
  'bg-indigo-500': '#6366F1',
  'bg-violet-500': '#8B5CF6',
  'bg-purple-500': '#A855F7',
  'bg-fuchsia-500': '#D946EF',
  'bg-pink-500': '#EC4899',
  'bg-rose-500': '#F43F5E',
  'bg-slate-500': '#64748B',
};

export const getColorHex = (colorClass: string): string => {
  return COLOR_MAP[colorClass] || '#3B82F6';
};

export const WeekCircle: React.FC<WeekCircleProps> = ({ completedDates, color, size = 48 }) => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - mondayOffset);

  const strokeWidth = 3.5;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const gap = 8;

  const arcPath = (index: number) => {
    const arcAngle = (360 - gap * 7) / 7;
    const startAngle = (index * (arcAngle + gap) - 90) * (Math.PI / 180);
    const endAngle = (index * (arcAngle + gap) + arcAngle - 90) * (Math.PI / 180);

    const x1 = center + radius * Math.cos(startAngle);
    const y1 = center + radius * Math.sin(startAngle);
    const x2 = center + radius * Math.cos(endAngle);
    const y2 = center + radius * Math.sin(endAngle);

    return `M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`;
  };

  const colorHex = getColorHex(color);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none">
      {[0, 1, 2, 3, 4, 5, 6].map(i => {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        const dateStr = format(date, 'yyyy-MM-dd');
        const isCompleted = completedDates.includes(dateStr);

        return (
          <path
            key={i}
            d={arcPath(i)}
            stroke={isCompleted ? colorHex : 'rgba(148, 163, 184, 0.3)'}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            fill="none"
          />
        );
      })}
    </svg>
  );
};
